import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { statusColor } from '../../constants/Palette';
import type { FirstSecondFieldsDataType } from '../../data-transformer/common-commands/common-data.interface';
import { DataExecutor } from '../../data-transformer/data-executor';
import type { DataCommandResponse } from '../../data-transformer/data-transformer.interface';
import { LineChartCommandFactory } from '../../data-transformer/line-chart/line-chart-component.factory';
import { formatWidgetDatesToLocale } from '../../helpers/dateFormater';
import { type ChartResponse, useLineChartQuery } from '../../redux/config/chart-data';
import type { WidgetConfigurationData } from '../../redux/config/widgetApi';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';
import OdinCustomDetailsWindow from '../CustomDetailsWindow/custom-details-window.component';
import CustomToolTip from '../CustomToolTip/CustomToolTip';
import ChartGenericWrapper from '../common/chart-generic-wrapper';
import { defaultLegendWrapperStyle, renderLegend } from '../common/custom-legend.component';
import { XAxisLineChart } from '../common/custom-X-axis.component';
import { ActiveDot } from '../common/line-active-dot.component';
import { getDotWithColor } from '../common/line-dot-base-props';
import { compactNumberFormatter } from '../common/tick-formatter';
import type { ChartProps } from './chart.interface';
import type { LegendPayloadOnClick } from './chart-events';
import { genericSort, includeLegend, SortDirection } from './generalFunctions/general-functions';
import { filterChartValueByLegend, roundHighest } from './generalFunctions/get-domaincharts';
import { transformBiDimensionalLineChart } from './generalFunctions/transformLineChart';
import { useXAxisTopSettings } from './hooks/use-x-axis-top-settings';
import useInvalidateChartResponse from './hooks/useInvalidateChartResponse';
import useStartEndDates from './hooks/useStartEndDates';

export interface DotPayloadProps {
  cy?: number;
  cx?: number;
  fill?: string;
  payload?: ModalDataProps;
  dataKey?: string;
  item?: string;
  onClick?: () => void;
}

export interface ModalDataProps {
  fieldValue?: string;
  field?: string;
}

export default function OdinLineChart({ widgetId, layoutProps, activateAnimation }: ChartProps) {
  const [legendProps, setLegendProps] = useState<ChartResponse & Record<string, unknown>>({});
  const [transformedData, setTransformedData] = useState<ChartResponse[]>();
  const [secondFieldKeys, setSecondFieldKeys] = useState<Array<string>>();
  const [detailsWindowVisible, setDetailsWindowVisible] = useState<boolean>(false);
  const [modalData, setModalData] = useState<ModalDataProps>();
  const [secondFieldValue, setSecondFieldValue] = useState<string | undefined>();
  const [configurationData, setConfigurationData] = useState<WidgetConfigurationData>();

  const { startDate, endDate } = useStartEndDates();
  const xAxisSettings = useXAxisTopSettings();
  const widgets = useSelector(getWidgets);

  const {
    data: widgetChartData,
    isLoading,
    isFetching: isWidgetChartDataFetching,
    isError,
    error,
  } = useLineChartQuery({
    widgetId,
    from: startDate,
    to: endDate,
  });

  const isSecondFieldUndefined = useMemo(() => {
    return (
      widgetChartData !== undefined &&
      widgetChartData.every((item) => {
        return item.secondField === undefined;
      })
    );
  }, [widgetChartData]);

  useEffect(() => {
    if (widgets) {
      setConfigurationData(widgets.find((widget) => +widget.widget.id === +widgetId)?.widget);
    }
  }, [widgets, widgetId]);

  useEffect(() => {
    if (transformedData && transformedData.length > 0) {
      setSecondFieldKeys(
        !isSecondFieldUndefined
          ? Object.keys(transformedData[0]).filter((key) => key !== 'field' && key !== 'value')
          : ['2']
      );
    }
  }, [transformedData, isSecondFieldUndefined]);

  useEffect(() => {
    const commandExecutor = new DataExecutor();
    if (widgetChartData && configurationData) {
      const dateFormattedData = formatWidgetDatesToLocale(configurationData, widgetChartData);
      if (isSecondFieldUndefined) {
        setTransformedData(transformBiDimensionalLineChart(dateFormattedData));
      } else {
        const commandFactory = new LineChartCommandFactory();
        let commandResponse:
          | DataCommandResponse<FirstSecondFieldsDataType, WidgetConfigurationData, FirstSecondFieldsDataType>
          | undefined;
        let outputData: FirstSecondFieldsDataType = dateFormattedData;
        while ((commandResponse = commandFactory.getNextCommand())) {
          const inputData: FirstSecondFieldsDataType = outputData;
          outputData = commandExecutor.execute(commandResponse.command, inputData, configurationData);
        }

        setTransformedData(outputData as ChartResponse[]);
      }
    }
  }, [widgetChartData, configurationData, isSecondFieldUndefined]);

  const selectItem = (e: LegendPayloadOnClick) => {
    setLegendProps({
      ...legendProps,
      [(e.dataKey as string | number | undefined) || (e.value as string)]:
        !legendProps[(e.dataKey as string | number | undefined) || (e.value as string)],
    });
  };

  /** The active dot(the dot the user is clicking),
   Documentation link: https://recharts.org/en-US/api/Line
   this method returns an active dot positioned at X and Y with a radius of R
   cx: The x-coordinate of center.
   cy: The y-coordinate of center.
   r: The radius of dot.
   * */
  const RepositoryGraphActiveDot = ({ cy, cx, fill, payload, dataKey, item }: DotPayloadProps) => {
    const dotOnClick = () => {
      setModalData(payload);
      setSecondFieldValue(dataKey !== 'Quantity' ? dataKey : undefined);
      setDetailsWindowVisible(true);
    };
    return <ActiveDot item={item} onClick={dotOnClick} cy={cy} cx={cx} fill={fill} />;
  };

  const onDialogHidden = () => {
    setModalData(undefined);
    setDetailsWindowVisible(false);
    setSecondFieldValue(undefined);
  };

  const handleRefetch = useInvalidateChartResponse(widgetId);

  const largestDomainValue: number = useMemo(() => {
    let largest = 0;
    const filteredData = filterChartValueByLegend(widgetChartData, legendProps);
    if (filteredData) {
      largest = Math.max(
        ...filteredData.map((item) => {
          if (item.value) {
            const parsedNumber = Number(item.value);
            if (!Number.isNaN(parsedNumber)) {
              return parsedNumber;
            }
          }
          return 0;
        })
      );
    }
    return roundHighest(largest);
  }, [widgetChartData, legendProps]);

  return (
    <ChartGenericWrapper
      isError={isError}
      error={error}
      isFetching={isWidgetChartDataFetching}
      isLoading={isLoading}
      refetch={handleRefetch}
      widgetId={widgetId}
      widgetConfigurationData={configurationData && { ...configurationData, layoutProps }}
      widgetChartData={widgetChartData}
      renderNoDataByDefault={!isWidgetChartDataFetching && !transformedData?.length}
    >
      {configurationData && widgetChartData && transformedData && transformedData.length > 0 && (
        <>
          <OdinCustomDetailsWindow
            {...configurationData.widgetData}
            widgetId={widgetId}
            fieldValue={modalData?.fieldValue || modalData?.field}
            secondFieldValue={secondFieldValue}
            visible={detailsWindowVisible}
            onDialogHidden={onDialogHidden}
          />
          <ResponsiveContainer height="99%">
            <ComposedChart
              data={
                isSecondFieldUndefined
                  ? transformedData
                  : genericSort(transformedData, 'field', SortDirection.Ascending)
              }
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 30,
              }}
              layout="horizontal"
            >
              <Tooltip content={<CustomToolTip />} cursor={false} isAnimationActive={false} />
              <>
                <CartesianGrid strokeDasharray={undefined} vertical={false} />
                <XAxis {...xAxisSettings} interval={0} tick={XAxisLineChart} />
                <YAxis
                  type="number"
                  domain={[0, largestDomainValue]}
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  label={{
                    value: 'Quantity',
                    angle: -90,
                    dx: -40,
                    position: 'center',
                  }}
                  tickFormatter={compactNumberFormatter}
                  width={undefined}
                  tick
                />
                {includeLegend(configurationData) && (
                  <Legend
                    formatter={renderLegend}
                    verticalAlign="bottom"
                    wrapperStyle={defaultLegendWrapperStyle}
                    onClick={selectItem}
                  />
                )}
                {secondFieldKeys?.map((item, index: number) => {
                  const name = isSecondFieldUndefined ? 'Quantity' : item;
                  return (
                    <Line
                      key={item}
                      name={name}
                      dataKey={isSecondFieldUndefined ? 'value' : item}
                      strokeWidth={2}
                      type="linear"
                      stroke={statusColor[index]}
                      fill={statusColor[index]}
                      hide={Boolean(legendProps && legendProps[isSecondFieldUndefined ? 'value' : item])}
                      dot={getDotWithColor(statusColor[index])}
                      activeDot={<RepositoryGraphActiveDot item={name} />}
                      isAnimationActive={activateAnimation}
                    />
                  );
                })}
              </>
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </ChartGenericWrapper>
  );
}
